/**
 * <sun-product-availability>
 * Toggle `.variant--soldout` sur `.sun-product__panel` et réaligne le label du
 * bouton submit au changement de variant / quantité.
 */
class SunProductAvailability extends HTMLElement {
  connectedCallback() {
    this.panel = this.closest('.sun-product__panel');
    this.productInfo = this.closest('product-info');
    if (!this.panel || !this.productInfo) return;

    this.sync();

    this.loadHandler = (event) => {
      if (event.target === this.productInfo) this.sync();
    };
    document.addEventListener('theme:product-info:loaded', this.loadHandler);

    if (typeof window.subscribe === 'function' && window.theme?.PUB_SUB_EVENTS?.variantChange) {
      this.variantChangeUnsubscribe = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, (payload) => {
        const data = payload?.data;
        if (!data) return;
        const sectionId = this.productInfo.dataset.section;
        if (sectionId && data.sectionId && data.sectionId !== sectionId) return;
        this.sync(data.variant);
        this.syncButtonText(data.html);
        this.syncPriceOff(data.html);
      });
    }

    this.qtyChangeHandler = (event) => {
      if (event.target?.matches?.('input[name="quantity"]')) {
        requestAnimationFrame(() => this.sync());
      }
    };
    this.productInfo.addEventListener('change', this.qtyChangeHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('theme:product-info:loaded', this.loadHandler);
    this.variantChangeUnsubscribe?.();
    if (this.qtyChangeHandler) {
      this.productInfo.removeEventListener('change', this.qtyChangeHandler);
    }
  }

  get sectionId() {
    return this.productInfo.dataset.originalSection || this.productInfo.dataset.section;
  }

  sync(variantOverride) {
    let variant = variantOverride;

    if (variant === undefined) {
      const script = this.productInfo.querySelector('[data-selected-variant]');
      if (!script) return;
      try {
        variant = JSON.parse(script.textContent);
      } catch (e) {
        return;
      }
    }

    let unavailable = !variant || variant.available === false;

    if (!unavailable && variant) {
      const rule = variant.quantity_rule || {};
      const min = rule.min || 1;
      const checkAgainstInventory =
        variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue';

      if (checkAgainstInventory) {
        const inventoryQuantity = variant.inventory_quantity;
        const qtyInput = this.productInfo.querySelector('input[name="quantity"]');
        const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        if (min > inventoryQuantity || qty > inventoryQuantity) {
          unavailable = true;
        }
      }
    }

    this.panel.classList.toggle('variant--soldout', unavailable);

    // Keep the hidden variant-id input in sync with availability. product-info.js
    // updates its *value* on variant change but never clears the SSR `disabled`
    // attribute (set in sun-product-buttons.liquid when the page loads on an
    // unavailable variant, e.g. via a sold-out `?variant=` URL). A disabled input
    // is dropped from `new FormData(form)`, so add-to-cart — and the dynamic
    // checkout button — would target no variant after switching from a sold-out
    // to an available variant. Toggle it here so the form always posts the
    // currently-selected variant.
    this.productInfo.querySelectorAll('.product-variant-id').forEach((input) => {
      input.toggleAttribute('disabled', unavailable);
    });
  }

  syncButtonText(html) {
    if (!html) return;
    const id = `ProductSubmitButton-${this.sectionId}`;
    const liveText = this.productInfo.querySelector(`#${id} [data-add-to-cart-text]`);
    const newText = html.getElementById(id)?.querySelector('[data-add-to-cart-text]');
    if (liveText && newText) {
      liveText.innerHTML = newText.innerHTML;
    }
  }

  // Réduction affichée sur la ligne catégorie (hors `#Price-*`, donc hors resynchro product-info.js).
  syncPriceOff(html) {
    if (!html) return;
    const id = `PriceOff-${this.sectionId}`;
    const live = this.productInfo.querySelector(`#${id}`);
    const next = html.getElementById(id);
    if (live && next) {
      live.innerHTML = next.innerHTML;
    }
  }
}

if (!customElements.get('sun-product-availability')) {
  customElements.define('sun-product-availability', SunProductAvailability);
}
